/**
 * Taxonomia canónica de famílias turísticas — módulo puro e partilhado.
 *
 * Independente de cidades, países e idiomas: nenhuma família tem regras por
 * destino, e nenhum nome de cidade/locar existe aqui. Cada família define:
 *  - intenções normalizadas multilingues (deteção do pedido);
 *  - termos de pesquisa no idioma do pedido + termos canónicos de fallback;
 *  - tipos compatíveis do fornecedor (Places Table A);
 *  - regras de relevância baseadas em tipos, nome e metadados — nunca numa
 *    etiqueta textual única.
 *
 * A família "viewpoints_panoramas" NÃO exige que o fornecedor devolva
 * literalmente "miradouro": um ponto panorâmico devolvido como atração
 * turística, parque, praça, monumento exterior ou observatório é elegível
 * quando os metadados (nome/tipos) confirmam a experiência de vista.
 *
 * Comida/restaurantes NÃO é família desta taxonomia: os fluxos de comida
 * (cozinha, doçaria, pequeno-almoço) pertencem ao motor legado protegido.
 */

export type TourismFamilyId =
  | "museums_culture"
  | "viewpoints_panoramas"
  | "monuments_heritage"
  | "nature_parks"
  | "water_beaches"
  | "neighbourhoods_markets"
  | "stadiums_architecture"
  | "activities_attractions"
  | "special_places";

/** Ordem canónica e estável — usada na deteção e na recolha. */
export const TOURISM_FAMILY_ORDER: TourismFamilyId[] = [
  "museums_culture",
  "viewpoints_panoramas",
  "monuments_heritage",
  "nature_parks",
  "water_beaches",
  "neighbourhoods_markets",
  "stadiums_architecture",
  "activities_attractions",
  "special_places",
];

const W = "(?<![\\p{L}])";
const Wend = "(?![\\p{L}])";
const re = (pattern: string) => new RegExp(pattern, "iu");

/** Prova negativa: a família foi mencionada com negação nas redondezas. */
const NEGATION = new RegExp(
  `${W}(n[aã]o|nem|sem|nunca|exclu[ií]|not|no|without|nicht|kein|ohne|sans|aucun|senza|nessun)${Wend}[\\s\\p{L}]{0,24}$`,
  "iu",
);

export type TourismFamilySpec = {
  id: TourismFamilyId;
  /** Intenção explícita do pedido, multilingue. */
  intent: RegExp;
  /** Termo canónico no idioma do pedido (primeira estratégia). */
  term: Partial<Record<"pt" | "en" | "es" | "fr" | "de" | "it", string>> & { en: string };
  /** Sinónimos canónicos de fallback (segunda estratégia), por ordem. */
  fallbackTerms: string[];
  /** Tipos compatíveis do fornecedor (terceira estratégia). */
  includedTypes: string[];
  /** Tipos do fornecedor que provam diretamente a família. */
  typeEvidence: RegExp;
  /** Nome que confirma a experiência (metadados de relevância). */
  nameEvidence: RegExp;
  /** Tipos/nomes que desqualificam quando não há prova direta. */
  disqualify?: RegExp;
};

export const TOURISM_FAMILIES: Record<TourismFamilyId, TourismFamilySpec> = {
  museums_culture: {
    id: "museums_culture",
    intent: re(`${W}(museus?|museums?|mus[eé]es?|museos?|galerias?\\s+de\\s+arte|art\\s+galler(?:y|ies)|pinacotecas?|centros?\\s+interpretativos?|exposi[cç][oõ]es?|exhibitions?)${Wend}`),
    term: { pt: "museus e centros culturais", en: "museums and cultural centres", es: "museos y centros culturales", fr: "musées et centres culturels", de: "Museen und Kulturzentren", it: "musei e centri culturali" },
    fallbackTerms: ["museums", "art galleries", "cultural centers"],
    includedTypes: ["museum", "art_gallery", "aquarium", "planetarium"],
    typeEvidence: re(`museum|art_gallery|aquarium|planetarium|art_center|cultural_center|cultural_landmark`),
    nameEvidence: re(`museu|museum|mus[eé]e|museo|galeria|gallery|pinacoteca|centro\\s+interpretativ|interpretation\\s+cent|kunsthaus|kunstmuseum`),
  },
  viewpoints_panoramas: {
    id: "viewpoints_panoramas",
    intent: re(`${W}(miradouros?|mirantes?|viewpoints?|view\\s?points?|overlooks?|lookouts?|miradores?|points?\\s+de\\s+vue|puntos?\\s+panor[aá]micos?|punti\\s+panoramici|aussichtspunkte?|belvédères?|vistas?\\s+panor[aâ]micas?|panoramic\\s+views?)${Wend}`),
    term: { pt: "miradouros e vistas panorâmicas", en: "viewpoints and panoramic spots", es: "miradores y vistas panorámicas", fr: "points de vue panoramiques", de: "Aussichtspunkte und Panoramen", it: "punti panoramici e viste" },
    fallbackTerms: ["scenic viewpoints", "observation decks", "panoramic terraces"],
    includedTypes: ["tourist_attraction", "park", "natural_feature"],
    typeEvidence: re(`viewpoint|observation_deck|scenic|tourist_attraction|park|natural_feature|rooftop`),
    nameEvidence: re(`miradou|mirante|view\\s?point|viewpoint|lookout|overlook|belvedere|belvédère|mirador|panoram|aussicht|punto\\s+panoram|vista\\s+panor|ponto\\s+de\\s+vista|observator|sky\\s?deck|skydeck|rooftop\\s+(?:bar\\s+)?view|terr[aá]cio\\s+panor`),
    // Um museu/sala fechada nunca prova uma vista sem evidência no nome.
    disqualify: re(`^museum$|^art_gallery$|^store$|^shopping_mall$|^restaurant$|^cafe$|^bar$|^lodging$`),
  },
  monuments_heritage: {
    id: "monuments_heritage",
    intent: re(`${W}(monumentos?|monuments?|patrim[oó]nio|heritage|landmarks?|castelos?|castles?|pal[aá]cios?|palaces?|torres?|towers?|fortes|fortalezas?|forts?|mosteiros?|monasteries?|catedrais?|cathedrals?|igrejas?|churches?|ru[ií]nas?|ruins?|memoriais?|memorials?|est[aá]tuas?|statues?|aquedutos?|aqueducts?|far[oó]is?|lighthouses?)${Wend}`),
    term: { pt: "monumentos e património histórico", en: "monuments and historic landmarks", es: "monumentos y patrimonio histórico", fr: "monuments et patrimoine historique", de: "Denkmäler und historische Wahrzeichen", it: "monumenti e patrimonio storico" },
    fallbackTerms: ["historic landmarks", "monuments", "castles and palaces"],
    includedTypes: ["historical_landmark", "monument", "castle", "tourist_attraction"],
    typeEvidence: re(`monument|memorial|historical_landmark|landmark|castle|palace|fortress|tower|ruins|arch|aqueduct|lighthouse|place_of_worship|church|cathedral|mosque|synagogue|hindu_temple|temple|bridge|monastery`),
    nameEvidence: re(`castelo|castle|forte|fortaleza|fort\\b|pal[aá]cio|palace|torre|tower|mosteiro|monastery|convent|igreja|church|catedral|cathedral|s[eé]\\s|templo|temple|monumento|memorial|est[aá]tua|statue|ru[ií]nas|ruins|aqueduto|aqueduct|farol|lighthouse|bas[ií]lica|basilica|mesquita|mosque|sinagoga|synagogue|ponte|bridge|arco\\b|arch\\b|landmark`),
  },
  nature_parks: {
    id: "nature_parks",
    intent: re(`${W}(parques?\\s+naturais?|parques?\\s+nacionais?|nature\\s+parks?|national\\s+parks?|reservas?\\s+naturais?|nature\\s+reserves?|jardins?|gardens?|trilhos?|trails?|cachoeiras?|cascatas?|waterfalls?|florestas?|forests?|montanhas?|mountains?)${Wend}`),
    term: { pt: "parques naturais e trilhos", en: "nature parks and trails", es: "parques naturales y senderos", fr: "parcs naturels et sentiers", de: "Naturparks und Wanderwege", it: "parchi naturali e sentieri" },
    fallbackTerms: ["parks and gardens", "nature reserves", "hiking areas"],
    includedTypes: ["park", "national_park", "natural_feature", "hiking_area", "botanical_garden"],
    typeEvidence: re(`park|garden|national_park|natural_feature|hiking_area|botanical_garden|nature_reserve|forest|mountain`),
    nameEvidence: re(`parque|park\\b|jardim|garden|floresta|forest|serra|montanha|mountain|reserva|reserve|trilho|trail|cascata|waterfall|lagoa|geopark|dunas|dunes|mata\\b|bosque|wood`),
  },
  water_beaches: {
    id: "water_beaches",
    intent: re(`${W}(praias?|beaches?|playas?|plages?|rios?|rivers?|lagos?|lakes?|lagoas?|lagoons?|orla\\s+mar[ií]tima|waterfronts?|passeios?\\s+à\\s+beira\\s-mar|marinas?|pieres?)${Wend}`),
    term: { pt: "praias, rios e orla marítima", en: "beaches, rivers and waterfronts", es: "playas, ríos y frentes marítimos", fr: "plages, rivières et fronts de mer", de: "Strände, Flüsse und Uferpromenaden", it: "spiagge, fiumi e lungomari" },
    fallbackTerms: ["beaches", "lakes and rivers", "seaside promenades"],
    includedTypes: ["beach", "marina", "pier", "promenade", "harbor"],
    typeEvidence: re(`beach|marina|lake|river|waterfront|pier|harbor|harbour|promenade`),
    // Sem "port\b": "Porto" (cidade) não pode provar família de água.
    nameEvidence: re(`praia|beach|playa|plage|rio\\b|river|lago\\b|lake|lagoa|mar\\b|sea\\b|costa|coast|marina|cais|pier|doca|dock|orla|waterfront`),
  },
  neighbourhoods_markets: {
    id: "neighbourhoods_markets",
    intent: re(`${W}(bairros?|neighbou?rhoods?|mercados?|markets?|feiras?|pra[çc]as?|squares?|plazas?|ruas?\\s+emblem[aá]ticas?|notable\\s+streets?|food\\s?halls?|bazares?|bazaars?)${Wend}`),
    term: { pt: "bairros, mercados e praças emblemáticas", en: "neighbourhoods, markets and notable squares", es: "barrios, mercados y plazas emblemáticas", fr: "quartiers, marchés et places emblématiques", de: "Viertel, Märkte und bekannte Plätze", it: "quartieri, mercati e piazze celebri" },
    fallbackTerms: ["local markets", "historic neighbourhoods", "town squares"],
    includedTypes: ["market", "shopping_mall", "neighborhood", "plaza", "town_square"],
    typeEvidence: re(`market|shopping_mall|neighborhood|plaza|square|town_square|food_hall|bazaar|promenade`),
    nameEvidence: re(`mercado|market|feira|pra[çc]a|square|plaza|bairro|rua\\s|street|rue\\b|bazarr|bazaar|almada\\s+market`),
  },
  stadiums_architecture: {
    id: "stadiums_architecture",
    intent: re(`${W}(est[aá]dios?|stadiums?|arenas?|pavilhões?|arquitetura|architecture|edif[ií]cios?\\s+ic[oô]nicos?|iconic\\s+buildings?|esta[çc][oõ]es?|stations?|opera|audit[oó]rios?|auditoriums?|c[aâ]maras?\\s+municipais?|city\\s+hall|town\\s+hall)${Wend}`),
    term: { pt: "estádios e arquitetura emblemática", en: "stadiums and iconic architecture", es: "estadios y arquitectura emblemática", fr: "stades et architecture emblématique", de: "Stadien und markante Architektur", it: "stadi e architettura iconica" },
    fallbackTerms: ["stadiums and arenas", "iconic buildings", "notable architecture"],
    includedTypes: ["stadium", "arena", "tourist_attraction"],
    typeEvidence: re(`stadium|arena|gym|architecture|cultural_landmark`),
    nameEvidence: re(`est[aá]dio|stadium|arena\\b|pavilhão|arquitet|architecture|c[aâ]mara\\s+municipal|city\\s+hall|town\\s+hall|esta[çc][aã]o\\b|station\\b|opera\\b|auditório|auditorium`),
  },
  activities_attractions: {
    id: "activities_attractions",
    intent: re(`${W}(atra[çc][oõ]es?|attractions?|atividades?|activities?|parques?\\s+de\\s+divers[oõ]es?|amusement\\s+parks?|parques?\\s+tem[aá]ticos?|theme\\s+parks?|zool[oó]gicos?|zoos?|aqu[aá]rios?|aquariums?|kart[oó]dromos?|go\\s?kart|telef[ée]ricos?|cable\\s+cars?|gondolas?|funiculares?|funiculars?|escape\\s+rooms?|parques?\\s+aqu[aá]ticos?|water\\s?parks?)${Wend}`),
    term: { pt: "atrações e atividades", en: "attractions and activities", es: "atracciones y actividades", fr: "attractions et activités", de: "Attraktionen und Aktivitäten", it: "attrazioni e attività" },
    fallbackTerms: ["tourist attractions", "amusement parks", "zoos and aquariums"],
    includedTypes: ["tourist_attraction", "amusement_park", "water_park", "zoo", "aquarium"],
    typeEvidence: re(`tourist_attraction|amusement_park|water_park|zoo|aquarium|casino|bowling|night_club|theme_park`),
    nameEvidence: re(`zoo|parque\\s+de\\s+divers|amusement|aquário|aquarium|kart|bowling|escape|telef[ée]rico|cable\\s+car|gondola|funicular|parque\\s+aqu[aá]tic|water\\s?park|laser|paintball|surf|parque\\s+tem[aá]tic|theme\\s?park`),
  },
  special_places: {
    id: "special_places",
    intent: re(`${W}(locais?\\s+especi[aá]is?|s[ií]tios?\\s+diferentes?|joias?\\s+escondidas?|p[eé]rolas?\\s+escondidas?|hidden\\s+gems?|secret\\s+spots?|local\\s+gems?|versteckte\\s+perlen|joyas?\\s+ocultas?|gioielli\\s+nascosti)${Wend}`),
    term: { pt: "locais especiais fora dos circuitos óbvios", en: "hidden gems and special local spots", es: "joyas ocultas y lugares especiales", fr: "perles cachées et lieux spéciaux", de: "versteckte Perlen und besondere Orte", it: "gioielli nascosti e luoghi speciali" },
    fallbackTerms: ["hidden gems", "off the beaten path attractions"],
    includedTypes: ["tourist_attraction"],
    typeEvidence: re(`tourist_attraction|landmark`),
    nameEvidence: re(`.`), // família permissiva: qualquer atração real serve de variedade
  },
};

/* ------------------------------------------------------------------ *
 * Deteção de intenções explícitas (multilingue, ordem estável)
 * ------------------------------------------------------------------ */

/** Devolve as famílias explicitamente pedidas, em ordem canónica estável. */
export function detectExplicitTourismFamilies(text: string | null | undefined): TourismFamilyId[] {
  if (!text?.trim()) return [];
  const out: TourismFamilyId[] = [];
  for (const family of TOURISM_FAMILY_ORDER) {
    const spec = TOURISM_FAMILIES[family];
    if (!spec.intent.test(text)) continue;
    // Negada ("sem museus", "no museums") não é um pedido explícito.
    const matches = text.match(new RegExp(spec.intent.source, spec.intent.flags.includes("g") ? spec.intent.flags : `${spec.intent.flags}g`));
    const negated = (matches ?? []).some((m) => {
      const idx = text.indexOf(m);
      return idx >= 0 && NEGATION.test(text.slice(0, idx + m.length));
    });
    if (negated) continue;
    out.push(family);
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * Estratégias de recolha por família (progressivas e ordenadas)
 * ------------------------------------------------------------------ */

const baseLanguage = (language: string | undefined): "pt" | "en" | "es" | "fr" | "de" | "it" => {
  const code = (language ?? "en").toLowerCase().split(/[-_]/)[0] ?? "en";
  return (["pt", "en", "es", "fr", "de", "it"] as const).includes(code as "pt")
    ? (code as "pt" | "en" | "es" | "fr" | "de" | "it")
    : "en";
};

export type FamilySearchStrategy =
  | { kind: "text"; query: string; origin: "request_language" | "canonical_fallback" }
  | { kind: "type"; includedType: string; origin: "provider_type" };

/**
 * Estratégias ORDENADAS de uma família. As seguintes só correm quando a
 * anterior não produziu candidatos elegíveis suficientes — nunca todas por
 * defeito (recolha progressiva, Parte G).
 */
export function planFamilyStrategies(family: TourismFamilyId, language: string | undefined): FamilySearchStrategy[] {
  const spec = TOURISM_FAMILIES[family];
  const strategies: FamilySearchStrategy[] = [];
  const lang = baseLanguage(language);
  strategies.push({ kind: "text", query: spec.term[lang] ?? spec.term.en!, origin: "request_language" });
  for (const fallback of spec.fallbackTerms) {
    strategies.push({ kind: "text", query: fallback, origin: "canonical_fallback" });
  }
  for (const includedType of spec.includedTypes) {
    strategies.push({ kind: "type", includedType, origin: "provider_type" });
  }
  return strategies;
}

/* ------------------------------------------------------------------ *
 * Relevância: tipos + nome + metadados (nunca uma etiqueta única)
 * ------------------------------------------------------------------ */

export type FamilyRelevance = {
  accepted: boolean;
  /** De onde veio a prova: tipo do fornecedor, nome/metadados, ou ambos. */
  evidence: "direct_type" | "name" | "type_and_name" | "none";
};

export type ClassifiablePlace = {
  name?: string | null;
  types?: string[] | null;
  primaryType?: string | null;
};

const fold = (value: string) =>
  (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase();

/**
 * Classifica um lugar para UMA família. Um lugar é elegível quando:
 *  - um tipo oficial do fornecedor prova diretamente a família; ou
 *  - o nome/metadados confirma a experiência (ex.: "Miradouro" num parque).
 * Tipos que desqualificam (ex.: museu fechado para vista) só eliminam quando
 * não há nenhuma prova de vista no nome.
 */
export function classifyPlaceForFamily(family: TourismFamilyId, place: ClassifiablePlace): FamilyRelevance {
  const spec = TOURISM_FAMILIES[family];
  const name = fold(place.name ?? "");
  const types = (place.types ?? []).map((t) => fold(t));
  const primary = fold(place.primaryType ?? "");
  const joinedTypes = [...types, ...(primary ? [primary] : [])].join(" ");
  const typeOk = !!joinedTypes && spec.typeEvidence.test(joinedTypes) && !(spec.disqualify && spec.disqualify.test(primary ?? ""));
  const nameOk = !!name && spec.nameEvidence.test(name);
  if (typeOk && nameOk) return { accepted: true, evidence: "type_and_name" };
  if (typeOk) return { accepted: true, evidence: "direct_type" };
  if (nameOk) return { accepted: true, evidence: "name" };
  return { accepted: false, evidence: "none" };
}

/** Um lugar pertence a QUALQUER uma das famílias pedidas? */
export function placeMatchesAnyTourismFamily(
  families: TourismFamilyId[],
  place: ClassifiablePlace,
): FamilyRelevance & { family: TourismFamilyId | null } {
  for (const family of families) {
    const relevance = classifyPlaceForFamily(family, place);
    if (relevance.accepted) return { ...relevance, family };
  }
  return { accepted: false, evidence: "none", family: null };
}

/** Rótulo localizado da família para trace/cartões (sem texto do pedido). */
const FAMILY_LABELS: Record<TourismFamilyId, Record<"pt" | "en" | "es" | "fr" | "de" | "it", string>> = {
  museums_culture: { pt: "museus e cultura", en: "museums and culture", es: "museos y cultura", fr: "musées et culture", de: "Museen und Kultur", it: "musei e cultura" },
  viewpoints_panoramas: { pt: "miradouros", en: "viewpoints", es: "miradores", fr: "points de vue", de: "Aussichtspunkte", it: "punti panoramici" },
  monuments_heritage: { pt: "monumentos e património", en: "monuments and heritage", es: "monumentos y patrimonio", fr: "monuments et patrimoine", de: "Denkmäler und Erbe", it: "monumenti e patrimonio" },
  nature_parks: { pt: "natureza e parques", en: "nature and parks", es: "naturaleza y parques", fr: "nature et parcs", de: "Natur und Parks", it: "natura e parchi" },
  water_beaches: { pt: "água e praias", en: "water and beaches", es: "agua y playas", fr: "eau et plages", de: "Wasser und Strände", it: "acqua e spiagge" },
  neighbourhoods_markets: { pt: "bairros e mercados", en: "neighbourhoods and markets", es: "barrios y mercados", fr: "quartiers et marchés", de: "Viertel und Märkte", it: "quartieri e mercati" },
  stadiums_architecture: { pt: "estádios e arquitetura", en: "stadiums and architecture", es: "estadios y arquitectura", fr: "stades et architecture", de: "Stadien und Architektur", it: "stadi e architettura" },
  activities_attractions: { pt: "atividades e atrações", en: "activities and attractions", es: "actividades y atracciones", fr: "activités et attractions", de: "Aktivitäten und Attraktionen", it: "attività e attrazioni" },
  special_places: { pt: "locais especiais", en: "special places", es: "lugares especiales", fr: "lieux spéciaux", de: "besondere Orte", it: "luoghi speciali" },
};

export function tourismFamilyLabel(family: TourismFamilyId, language: string | undefined): string {
  const lang = baseLanguage(language);
  return FAMILY_LABELS[family][lang];
}
